/**
 * TTEBadge — compact Time-to-Exploit badge.
 *
 * Shows the predicted exploitation window (predicted_tte_days) with a colour
 * keyed to urgency (red ≤2d / amber ≤7d / blue >7d) and a ⚡ when the window was
 * collapsed by the AI-discovered accelerant. Tooltip lists the accelerant
 * factors. Used on the vuln-triage list and anywhere a CVE row wants the
 * "weaponised soonest" signal inline.
 *
 * Thresholds MUST stay consistent with TTEvsMTTR + every other TTE surface:
 *   red ≤ 2d   amber ≤ 7d   blue > 7d
 */
import { Zap } from 'lucide-react'
import { clsx } from 'clsx'

export interface TTEData {
  predicted_tte_days: number
  our_mttr_days?: number
  exposure_days?: number
  losing?: boolean
  factors?: string[]
  confidence_band?: string | null
  ai_accelerated?: boolean
  verdict?: string
}

export function tteTone(days: number | null | undefined): 'red' | 'amber' | 'blue' {
  if (days == null) return 'blue'
  if (days <= 2) return 'red'
  if (days <= 7) return 'amber'
  return 'blue'
}

const TONE_CLS: Record<string, string> = {
  red:   'bg-rose/10 text-rose-light border-rose/30',
  amber: 'bg-amber/10 text-amber-light border-amber/30',
  blue:  'bg-blue/10 text-blue-light border-blue/30',
}

export default function TTEBadge({ tte, className }: { tte: TTEData | null | undefined; className?: string }) {
  if (!tte || tte.predicted_tte_days == null) {
    return <span className={clsx('text-[10px] text-text-muted', className)}>—</span>
  }
  const d = tte.predicted_tte_days
  const tone = tteTone(d)
  const label = d <= 0 ? 'now' : d < 1 ? `${(d * 24).toFixed(0)}h` : `${d % 1 === 0 ? d : d.toFixed(1)}d`
  const tip = [
    tte.verdict || `Predicted exploit window ≈ ${label}`,
    ...(tte.factors && tte.factors.length ? ['', 'Accelerants:', ...tte.factors.map(f => `· ${f}`)] : []),
  ].join('\n')
  return (
    <span
      className={clsx('inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border whitespace-nowrap',
        TONE_CLS[tone], className)}
      title={tip}
    >
      {tte.ai_accelerated && <Zap size={9} className="shrink-0" />}
      {label}
    </span>
  )
}
